import React, { useCallback } from 'react'
import { useStore } from '@xyflow/react'
import { canvasMapViewportBox, unionRect, type MapRect } from './utils/canvasMapViewport'

interface CanvasMapViewportProps {
  /** Die Maße der Karte — dieselben, die auch React Flow bekommt. */
  width: number
  height: number
}

/**
 * **Der Ausschnitt als Rahmen auf der Karte.**
 *
 * React Flow zeichnet ihn selbst, aber als Teil seiner Maske: EIN Pfad aus zwei
 * Rechtecken, ohne `rx`. Eine Rundung ist daran nicht zu haben, und ein Strich
 * an diesem Pfad trifft immer auch den äußeren Rahmen. Deshalb ein eigenes
 * Element darüber, mit `border-radius` wie jedes andere in der App — die Maske
 * der Bibliothek ist im Stylesheet abgeschaltet.
 *
 * **Eigene Komponente und nicht Teil von `CanvasMap`:** Sie hängt an Werten,
 * die sich bei JEDEM Bild ändern (die Verschiebung des Ausschnitts), und wird
 * deshalb je Bild neu gerendert. Als eigenes Bauteil trifft das nur sie — und
 * nur, solange die Karte überhaupt zu sehen ist, denn sonst steht sie gar nicht
 * im Baum.
 */
export const CanvasMapViewport: React.FC<CanvasMapViewportProps> = ({ width, height }) => {
  const select = useCallback((state: {
    transform: [number, number, number]
    width: number
    height: number
    nodeLookup: Map<string, {
      internals: { positionAbsolute: { x: number; y: number } }
      measured?: { width?: number; height?: number }
      hidden?: boolean
    }>
  }) => {
    const [tx, ty, zoom] = state.transform
    if (!zoom || !state.width || !state.height) return null

    const viewport: MapRect = {
      x: -tx / zoom,
      y: -ty / zoom,
      width: state.width / zoom,
      height: state.height / zoom,
    }

    // Die Ausdehnung ist die Vereinigung aus Elementen und Ausschnitt —
    // dieselbe Regel, nach der die Bibliothek ihre viewBox aufspannt.
    let bounds: MapRect | null = null
    for (const [, node] of state.nodeLookup) {
      const w = node.measured?.width
      const h = node.measured?.height
      if (!w || !h || node.hidden) continue
      const { x, y } = node.internals.positionAbsolute
      bounds = unionRect(bounds, { x, y, width: w, height: h })
    }

    return canvasMapViewportBox(unionRect(bounds, viewport), viewport, { width, height })
  }, [width, height])

  // Ein flacher Vergleich genügt: Der Selektor gibt vier Zahlen zurück, und
  // ein neues Objekt je Bild ist billiger als das Rendern, das es spart.
  const box = useStore(select, (a, b) =>
    a === b || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height))

  if (!box) return null

  return (
    <div
      className="canvas-map__viewport"
      aria-hidden="true"
      style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
    />
  )
}
